import { type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import type { ProposalBuildStatus } from '@shared/api';
import { type AuthStore, useAuth } from '../state/useAuth';
import { Sidebar } from './sidebar/Sidebar';

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'viq-sidebar-collapsed';
const SIDEBAR_WIDTH_STORAGE_KEY = 'viq-sidebar-width';
const SIDEBAR_DEFAULT_WIDTH = 276;
const SIDEBAR_MIN_WIDTH = 180;
const SIDEBAR_MAX_WIDTH = 420;
const SIDEBAR_COLLAPSE_THRESHOLD = 156;

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function readSidebarWidth(): number {
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (raw === null || raw.trim() === '') return SIDEBAR_DEFAULT_WIDTH;
    const saved = Number(raw);
    return Number.isFinite(saved) ? Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, saved)) : SIDEBAR_DEFAULT_WIDTH;
  } catch {
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

export function AppShell({
  auth,
  activeProposalId,
  advertiserName,
  buildStatus,
  buildFocus = false,
  children,
}: {
  auth: AuthStore;
  /** The proposal whose row should read as active in the sidebar's recent list. */
  activeProposalId?: string | null;
  /** Triggers a cheap re-fetch of the recent list when a proposal is renamed. */
  advertiserName?: string | null;
  buildStatus?: ProposalBuildStatus;
  /** Gives the build experience the full narrow viewport instead of squeezing it beside the desktop sidebar. */
  buildFocus?: boolean;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [sidebarWidth, setSidebarWidth] = useState(readSidebarWidth);
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
      // Intentionally silent if storage is unavailable.
    }
  }, [collapsed]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth));
    } catch {
      // Intentionally silent if storage is unavailable.
    }
  }, [sidebarWidth]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === '\\') {
        event.preventDefault();
        setCollapsed((value) => !value);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const setWidthFromPointer = (width: number) => {
    if (width < SIDEBAR_COLLAPSE_THRESHOLD) {
      setCollapsed(true);
      return;
    }
    setSidebarWidth(Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width)));
    setCollapsed(false);
  };

  const onResizeStart = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    const originX = event.clientX;
    const originWidth = sidebarWidth;
    setResizing(true);
    handle.setPointerCapture(event.pointerId);

    const onMove = (moveEvent: globalThis.PointerEvent) => {
      setWidthFromPointer(originWidth + moveEvent.clientX - originX);
    };
    const onEnd = () => {
      handle.removeEventListener('pointermove', onMove);
      setResizing(false);
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd, { once: true });
    handle.addEventListener('pointercancel', onEnd, { once: true });
  };

  const onResizeKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const step = event.shiftKey ? 32 : 16;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setWidthFromPointer(sidebarWidth - step);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setWidthFromPointer(sidebarWidth + step);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setCollapsed(true);
    } else if (event.key === 'End') {
      event.preventDefault();
      setSidebarWidth(SIDEBAR_MAX_WIDTH);
      setCollapsed(false);
    }
  };

  const appShellStyle = {
    '--viq-sidebar-w': collapsed ? '60px' : `${sidebarWidth}px`,
  } as CSSProperties;

  return (
    <div
      className={`app-shell${buildFocus ? ' app-shell-build-focus' : ''}${collapsed ? ' app-shell-sidebar-collapsed' : ''}${resizing ? ' app-shell-resizing' : ''}`}
      style={appShellStyle}
    >
      <Sidebar
        auth={auth}
        activeProposalId={activeProposalId ?? null}
        advertiserName={advertiserName ?? null}
        buildStatus={buildStatus}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((value) => !value)}
      />
      <button
        type="button"
        role="separator"
        className="sidebar-resizer"
        aria-label="Resize sidebar"
        aria-orientation="vertical"
        aria-valuemin={SIDEBAR_MIN_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        aria-valuenow={sidebarWidth}
        aria-valuetext={`${sidebarWidth} pixels`}
        onPointerDown={onResizeStart}
        onKeyDown={onResizeKeyDown}
      />
      <div className="app-shell-main">{children}</div>
    </div>
  );
}

/** What the current page tells the persistent shell about itself. */
export interface ShellState {
  activeProposalId?: string | null;
  advertiserName?: string | null;
  buildStatus?: ProposalBuildStatus;
  buildFocus?: boolean;
}

const ShellStateContext = createContext<(state: ShellState) => void>(() => {});

/**
 * The app frame, mounted once above the router's page switch so clicking
 * between proposals swaps only the main area — the sidebar keeps its list,
 * scroll, search and width instead of being torn down and rebuilt.
 * Pages describe themselves with `useShellState`.
 */
export function PersistentAppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [state, setState] = useState<ShellState>({});
  return (
    <ShellStateContext.Provider value={setState}>
      <AppShell auth={auth} {...state}>{children}</AppShell>
    </ShellStateContext.Provider>
  );
}

/** A page's report to the persistent shell; layout effect so the active row never lags a paint. */
export function useShellState({ activeProposalId = null, advertiserName = null, buildStatus, buildFocus = false }: ShellState): void {
  const setState = useContext(ShellStateContext);
  useLayoutEffect(() => {
    setState({ activeProposalId, advertiserName, buildStatus, buildFocus });
  }, [setState, activeProposalId, advertiserName, buildStatus, buildFocus]);
}
